import { Injectable, computed, inject } from '@angular/core';
import { cancelableResource, QueryBuilder, QueryFilter } from '@softline/core';
import { SOFTLINE_FILTER_WWS_ABC_ANALYSIS } from '../abc-analysis.api';
import { buildSegmentSummary } from '../functions/abc-analysis-segment-summary.function';
import { AbcAnalysisOverviewRow } from '../types/abc-analysis-overview-row';
import { AbcAnalysisSegmentAggregateRow } from '../types/abc-analysis-segment-aggregate-row';
import { AbcAnalysisFilterStateService } from './abc-analysis-filter-state.service';
import { AbcAnalysisOverviewGridService } from './abc-analysis-overview-grid.service';

// The raw shape the aggregated/grouped query actually returns: snake_case backend field
// names, with `product_number` holding a `count` aggregation result (there's no dedicated
// article-count column, so counting the product_number column is the way to get it). The
// backend serializes it as a string (its native article-number type) even under `count`,
// so it needs explicit Number() coercion below — unlike the other aggregate fields.
interface RawSegmentAggregateRow {
  segment: string;
  segment_color: number | null;
  product_number: string;
  net: number;
  sales_quantity: number;
  contribution_margin: number;
  inventory_value: number;
  inventory_quantity: number;
}

function toAggregateRow(raw: RawSegmentAggregateRow): AbcAnalysisSegmentAggregateRow {
  return {
    segment: raw.segment,
    segmentColor: raw.segment_color,
    articleCount: Number(raw.product_number),
    net: raw.net,
    salesQuantity: raw.sales_quantity,
    contributionMargin: raw.contribution_margin,
    inventoryValue: raw.inventory_value,
    inventoryQuantity: raw.inventory_quantity,
  };
}

@Injectable()
export class AbcAnalysisOverviewDataService {
  private readonly overviewGridService = inject(AbcAnalysisOverviewGridService);
  private readonly filterState = inject(AbcAnalysisFilterStateService);
  private readonly entityFilterState = inject(SOFTLINE_FILTER_WWS_ABC_ANALYSIS);

  private readonly overviewResource = cancelableResource({
    params: () => ({
      dateRangeFilter: this.filterState.dateRangeFilter(),
      filter: this.entityFilterState.queryFilter(),
      master: this.filterState.master(),
    }),
    loader: async ({ params }) => {
      const q = new QueryBuilder<AbcAnalysisOverviewRow>()
        .start(0)
        .end(4)
        .column('segment', { usage: 'group' })
        .column('segment_color', { usage: 'group' })
        .column('product_number', { usage: 'aggregate', agg: 'count' })
        .column('net', { usage: 'aggregate', agg: 'sum' })
        .column('sales_quantity', { usage: 'aggregate', agg: 'sum' })
        .column('contribution_margin', { usage: 'aggregate', agg: 'sum' })
        .column('inventory_value', { usage: 'aggregate', agg: 'sum' })
        .column('inventory_quantity', { usage: 'aggregate', agg: 'sum' })
        .sort('segment', 'DESC')
        .filter(params.dateRangeFilter);
      // The filter's field names (sales_organisation/supplier/...) are real abc-analysis
      // backend filter names but aren't part of AbcAnalysisOverviewRow (the backend keeps
      // filter names and output columns in separate namespaces — see
      // abc-analysis-filter-row.ts), hence the cast.
      if (params.filter) q.filter(params.filter as QueryFilter<AbcAnalysisOverviewRow>);
      const result = await this.overviewGridService.load({
        query: q.build(),
        master: params.master,
      });
      return ((result?.data ?? []) as unknown as RawSegmentAggregateRow[]).map(toAggregateRow);
    },
  });

  readonly overviewRows = computed(() => {
    if (this.overviewResource.status() === 'error') return [];
    return this.overviewResource.value() ?? [];
  });
  readonly hasError = computed(() => this.overviewResource.status() === 'error');
  readonly isLoading = this.overviewResource.isLoading;
  readonly segmentSummaryRows = computed(() => buildSegmentSummary(this.overviewRows()));
  readonly filteredArticleCount = computed(() =>
    this.overviewRows().reduce((sum, row) => sum + row.articleCount, 0)
  );
}
